import { getRouteApi, Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { ArrowLeftRight, Check, Fuel, Gauge, Hourglass, Plus, RefreshCw, SearchX, TriangleAlert, WifiOff } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';

import { Botao } from '@/componentes/Botao';
import { CabecalhoPagina } from '@/componentes/CabecalhoPagina';
import { Campo, CampoSelecao } from '@/componentes/Campo';
import { CardIndicador } from '@/componentes/CardIndicador';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { FaixaIndicadores } from '@/componentes/FaixaIndicadores';
import { Medida } from '@/componentes/Medida';
import { DadoChave, PainelDetalhe } from '@/componentes/PainelDetalhe';
import { RodapeTabela, Tabela, TextoDaCelula, type Coluna } from '@/componentes/Tabela';
import { formatarAteDuasCasas, formatarDecimal, formatarDia, formatarInteiro } from '@/lib/formato';
import { explicarFalha } from '@/lib/pedido';
import { useSessao } from '@/modulos/sessao/consultas';

import { useConciliacaoDaTelemetria, useDispositivo, useFila, useIntegracoes, useReprocessar, useSinal } from './api';
import {
  JanelaDaChaveNova,
  JanelaDasRegras,
  JanelaDeConferencia,
  JanelaDeDescarte,
  JanelaDeDispositivo,
  JanelaDeEncerrar,
  JanelaDeIntegracao,
  JanelaDeLigar,
  JanelaDeRevogacao,
} from './JanelasDaTelemetria';
import { SeloDaTelemetria } from './Selo';
import {
  EXEMPLO_DO_FORMATO,
  FILTROS_DA_FILA,
  FORMATOS,
  ROTA_DA_ENTRADA,
  ROTULO_DO_FILTRO,
  ROTULO_DO_TIPO,
  SITUACAO_DO_SINAL,
  conferenciaEscrita,
  divergenciaEscrita,
  oQueFazer,
  periodoEscrito,
  podeReprocessar,
  quando,
  rotuloDoTipo,
  situacaoNaFila,
  textoDoLimite,
  valorEscrito,
  type ChaveDaIntegracao,
  type ConsumoConciliado,
  type DiaConciliado,
  type DispositivoCadastrado,
  type DispositivoNoSinal,
  type FiltroDaFila,
  type Integracao,
  type LoteRecebido,
  type MensagemDaFila,
} from './telemetria';

/**
 * Módulo 17 do v6 — TELEMETRIA E SENSORES, na mesa (0103).
 *
 * Um hub com quatro abas, cada uma com endereço próprio, como o combustível:
 * o SINAL (cada dispositivo, a máquina e quando falou), a FILA (a área de
 * estágio: o que chegou e não entrou, com o que fazer), a CONCILIAÇÃO (horas de
 * motor da telemetria × apontadas; litros telemetrados × abastecidos) e as
 * INTEGRAÇÕES (provedor, chave, dispositivo e vínculo).
 *
 * Não há tela-modelo do módulo 17 no manual (o mapa das p.50 a p.60 não o
 * desenha): segue o hub com abas (p.40) e a lista com detalhe (p.38) — dúvida
 * de design registrada. Não há telemetria real ainda: sem mensagem, a tela diz
 * que não há.
 */
const ABAS = [
  {
    para: '/operacao/telemetria',
    rotulo: 'Sinal',
    explicacao:
      'Cada dispositivo, a máquina em que está e quando falou pela última vez. Sem sinal não é máquina parada: o apontamento e a leitura de campo continuam valendo.',
  },
  {
    para: '/operacao/telemetria/fila',
    rotulo: 'Fila',
    explicacao:
      'A área de estágio: tudo chega aqui e só entra depois de conferido. A fila mostra o que não entrou — pendente, recusado, suspeito ou recusado pela máquina —, com o que fazer.',
  },
  {
    para: '/operacao/telemetria/conciliacao',
    rotulo: 'Conciliação',
    explicacao:
      'As horas de motor da telemetria ao lado das apontadas, e o combustível consumido ao lado do abastecido. Hora de motor não é produção nem hora faturável.',
  },
  {
    para: '/operacao/telemetria/integracoes',
    rotulo: 'Integrações',
    explicacao: 'Os provedores, as chaves, os dispositivos e em que máquina cada um está, com a conferência do medidor.',
  },
] as const;

function abaDoCaminho(caminho: string): (typeof ABAS)[number] {
  return [...ABAS].reverse().find((a) => caminho === a.para || caminho.startsWith(`${a.para}/`)) ?? ABAS[0];
}

function usePermissoes() {
  const sessao = useSessao();
  const p = sessao.data?.permissoes ?? [];
  return { conferir: p.includes('telemetria.conferir'), gerir: p.includes('telemetria.gerir') };
}

function ErroDaLista({ oQue, erro, aoTentar }: { readonly oQue: string; readonly erro: unknown; readonly aoTentar: () => void }) {
  return (
    <EstadoVazio
      icone={TriangleAlert}
      tom="erro"
      titulo={`Não foi possível carregar ${oQue}`}
      descricao={explicarFalha(erro).comoResolver}
      acao={
        <Botao nivel="secundario" onClick={aoTentar}>
          Carregar de novo
        </Botao>
      }
    />
  );
}

/* ================================================================ o hub */

export function HubDaTelemetria({ children, acoes }: { readonly children: ReactNode; readonly acoes?: ReactNode }) {
  const caminho = useRouterState({ select: (e) => e.location.pathname });
  const navegar = useNavigate();
  const aba = abaDoCaminho(caminho);
  const sinal = useSinal();
  const ind = sinal.data?.indicadores;
  return (
    <div className="min-w-0 flex-1 overflow-auto">
      <div className="pagina">
        <CabecalhoPagina
          sobretitulo="Operação"
          titulo="Telemetria"
          subtitulo="O que os dispositivos das máquinas mandam, conferido antes de valer"
          {...(acoes ? { acoes } : {})}
        />
        <FaixaIndicadores>
          <CardIndicador
            rotulo="Dispositivos em máquina"
            valor={ind ? formatarInteiro(ind.ligados) : ''}
            contexto="ligados a uma máquina agora"
            icone={Gauge}
            tom="marca"
            carregando={!ind}
            aoAbrir={() => {
              void navegar({ to: '/operacao/telemetria' });
            }}
          />
          <CardIndicador
            rotulo="Sem sinal"
            valor={ind ? formatarInteiro(ind.semSinal) : ''}
            contexto="calados além da frequência; não quer dizer parada"
            icone={WifiOff}
            tom="standby"
            alerta={(ind?.semSinal ?? 0) > 0}
            carregando={!ind}
            aoAbrir={() => {
              void navegar({ to: '/operacao/telemetria' });
            }}
          />
          <CardIndicador
            rotulo="Na fila"
            valor={ind ? formatarInteiro(ind.naFila) : ''}
            contexto="mensagens que pedem alguém"
            icone={Hourglass}
            tom="standby"
            carregando={!ind}
            aoAbrir={() => {
              void navegar({ to: '/operacao/telemetria/fila' });
            }}
          />
          <CardIndicador
            rotulo="Códigos de falha"
            valor={ind ? formatarInteiro(ind.falhas24h) : ''}
            contexto="mandados pelas máquinas nas últimas 24 horas"
            icone={TriangleAlert}
            tom="parada"
            alerta={(ind?.falhas24h ?? 0) > 0}
            carregando={!ind}
          />
        </FaixaIndicadores>
        {/* O TRILHO DE ABAS SAIU (pedido do dono, 25/10/2026: *"Eu não quero
            mais abas internas"*). As telas deste módulo são seções da
            navegação lateral, que é a navegação do sistema. A linha que
            explica a tela aberta ficou: ela diz o que esta tela responde, e
            não era parte do trilho. */}
        <p className="txt-apoio max-w-[80ch] text-pretty text-tinta-secundaria">{aba.explicacao}</p>
        {children}
      </div>
    </div>
  );
}

/* ================================================================ o sinal */

const COLUNAS_DO_SINAL: readonly Coluna<DispositivoNoSinal>[] = [
  {
    chave: 'maquina',
    rotulo: 'Máquina',
    celula: (d) => (
      <span className="flex flex-col">
        <span className="codigo text-tinta">{d.equipamento ?? 'Sem máquina'}</span>
        <TextoDaCelula texto={d.maquina ?? 'Ligue o dispositivo a uma máquina'} classe="txt-apoio text-tinta-secundaria" />
      </span>
    ),
  },
  {
    chave: 'dispositivo',
    rotulo: 'Dispositivo',
    celula: (d) => (
      <span className="flex flex-col">
        <span className="codigo text-tinta">{d.identificador}</span>
        <TextoDaCelula texto={d.integracao} classe="txt-apoio text-tinta-secundaria" />
      </span>
    ),
  },
  {
    chave: 'sinal',
    rotulo: 'Último sinal',
    celula: (d) => (
      <span className="flex flex-col">
        <span className="txt-corpo tabular-nums text-tinta">{quando(d.ultimoSinalEm)}</span>
        <TextoDaCelula texto={textoDoLimite(d.limiteMin)} classe="txt-apoio text-tinta-secundaria" />
      </span>
    ),
  },
  {
    chave: 'horimetro',
    rotulo: 'Horímetro',
    alinhamento: 'numero',
    celula: (d) => (d.horimetro === null ? <span className="txt-apoio text-tinta-secundaria">—</span> : <Medida valor={formatarAteDuasCasas(d.horimetro)} unidade="h" />),
  },
  {
    chave: 'posicao',
    rotulo: 'Posição',
    prioridade: 'secundaria',
    celula: (d) =>
      d.posicao === null ? (
        <span className="txt-apoio text-tinta-secundaria">—</span>
      ) : (
        <span className="flex flex-col">
          <span className="txt-corpo tabular-nums text-tinta">{valorEscrito('posicao', { valor: null, ...d.posicao })}</span>
          <TextoDaCelula texto={quando(d.posicao.em)} classe="txt-apoio text-tinta-secundaria" />
        </span>
      ),
  },
  {
    chave: 'situacao',
    rotulo: 'Situação',
    alinhamento: 'status',
    celula: (d) => <SeloDaTelemetria {...SITUACAO_DO_SINAL[d.situacao]} />,
  },
];

export function PaginaSinalDaTelemetria() {
  const sinal = useSinal();
  const pode = usePermissoes();
  const [aberto, setAberto] = useState<string | null>(null);
  const [conferindo, setConferindo] = useState<DispositivoNoSinal | null>(null);
  const linhas = sinal.data?.dispositivos ?? [];
  const selecionado = linhas.find((d) => d.dispositivoId === aberto) ?? null;
  return (
    <>
      <HubDaTelemetria>
        <Tabela
          rotulo="Dispositivos e sinal"
          colunas={COLUNAS_DO_SINAL}
          linhas={linhas}
          chaveDaLinha={(d) => d.dispositivoId}
          alturaDupla
          selecionada={aberto}
          aoSelecionar={(d) => { setAberto(d.dispositivoId); }}
          carregando={sinal.isPending}
          erro={sinal.isError ? <ErroDaLista oQue="o sinal dos dispositivos" erro={sinal.error} aoTentar={() => { void sinal.refetch(); }} /> : null}
          vazio={
            <EstadoVazio
              icone={WifiOff}
              titulo="Nenhum dispositivo cadastrado"
              descricao={
                pode.gerir
                  ? 'A telemetria ainda não manda nada para esta empresa. Cadastre a integração e o dispositivo em Integrações, e ligue-o à máquina.'
                  : 'A telemetria ainda não manda nada para esta empresa. Quem cadastra a integração é o perfil Administrador.'
              }
            />
          }
          rodape={<p className="txt-apoio border-t border-borda-sutil px-4 py-2 text-tinta-secundaria">{`${formatarInteiro(linhas.length)} ${linhas.length === 1 ? 'dispositivo' : 'dispositivos'}`}</p>}
        />
      </HubDaTelemetria>
      {selecionado ? (
        <DetalheDoDispositivo
          key={selecionado.dispositivoId}
          d={selecionado}
          podeConferir={pode.conferir}
          aoConferir={() => { setConferindo(selecionado); }}
          aoFechar={() => { setAberto(null); }}
        />
      ) : null}
      {pode.conferir ? (
        <JanelaDeConferencia
          alvo={
            conferindo?.vinculoId && conferindo.equipamento
              ? { vinculoId: conferindo.vinculoId, equipamento: conferindo.equipamento, medidor: conferindo.medidor, ultimoContador: conferindo.medidor === 'odometro' ? null : conferindo.horimetro }
              : null
          }
          aoFechar={() => { setConferindo(null); }}
        />
      ) : null}
    </>
  );
}

function DetalheDoDispositivo({
  d,
  podeConferir,
  aoConferir,
  aoFechar,
}: {
  readonly d: DispositivoNoSinal;
  readonly podeConferir: boolean;
  readonly aoConferir: () => void;
  readonly aoFechar: () => void;
}) {
  const detalhe = useDispositivo(d.dispositivoId);
  const dd = detalhe.data;
  const carregando = (
    <div className="flex flex-col gap-2" aria-hidden="true">
      <span className="esqueleto block h-4 w-2/3" />
      <span className="esqueleto block h-4 w-1/2" />
    </div>
  );
  return (
    <PainelDetalhe
      codigo={d.identificador}
      titulo={d.equipamento ? `${d.identificador} em ${d.equipamento}` : d.identificador}
      subtitulo={d.integracao}
      marcadores={<SeloDaTelemetria {...SITUACAO_DO_SINAL[d.situacao]} />}
      dadosChave={
        <>
          <DadoChave rotulo="Último sinal">{quando(d.ultimoSinalEm)}</DadoChave>
          <DadoChave rotulo="Dado mais recente">{quando(d.ultimaLeituraEm, 'Nenhum')}</DadoChave>
          <DadoChave rotulo="Na máquina desde">{d.desde ? quando(d.desde) : '—'}</DadoChave>
          <DadoChave rotulo="Medidor">{d.conferidoEm ? `Conferido em ${quando(d.conferidoEm)}` : 'Não conferido'}</DadoChave>
        </>
      }
      abas={[
        {
          id: 'ultimos',
          rotulo: 'Últimos sinais',
          conteudo: !dd ? (
            carregando
          ) : dd.ultimos.length === 0 ? (
            <p className="txt-apoio text-tinta-secundaria">Nenhuma mensagem aceita deste dispositivo ainda.</p>
          ) : (
            <dl className="flex flex-col">
              {dd.ultimos.map((u) => (
                <div key={u.tipo} className="flex items-baseline justify-between gap-3 border-t border-borda-sutil py-2 first:border-t-0">
                  <dt className="txt-corpo text-tinta">{rotuloDoTipo(u.tipo)}</dt>
                  <dd className="flex flex-col items-end">
                    <span className="txt-corpo tabular-nums text-tinta">{valorEscrito(u.tipo, u)}</span>
                    <span className="txt-apoio text-tinta-secundaria">{`${quando(u.lidaEm)}${u.qualidade === 'suspeita' ? ' · suspeita' : ''}`}</span>
                  </dd>
                </div>
              ))}
            </dl>
          ),
        },
        {
          id: 'falhas',
          rotulo: 'Falhas',
          conteudo: !dd ? (
            carregando
          ) : dd.falhas.length === 0 ? (
            <p className="txt-apoio text-tinta-secundaria">O equipamento não mandou código de falha.</p>
          ) : (
            <ul className="flex flex-col">
              {dd.falhas.map((f) => (
                <li key={`${f.codigo ?? ''}-${f.lidaEm}`} className="flex flex-col gap-1 border-t border-borda-sutil py-2 first:border-t-0">
                  <span className="codigo text-tinta">{`${f.codigo ?? 'Sem código'}${f.severidade ? ` · ${f.severidade === 'media' ? 'média' : f.severidade}` : ''}`}</span>
                  <span className="txt-apoio text-tinta-secundaria">{`${f.descricao ?? 'Sem descrição'} · ${quando(f.lidaEm)}`}</span>
                </li>
              ))}
              <li className="txt-apoio pt-2 text-tinta-secundaria">É sinal para investigar, não diagnóstico: confira a máquina e, se for o caso, abra a O.S.</li>
            </ul>
          ),
        },
        {
          id: 'vinculos',
          rotulo: 'Máquinas',
          conteudo: !dd ? (
            carregando
          ) : (
            <ul className="flex flex-col">
              {dd.vinculos.map((v) => (
                <li key={v.id} className="flex flex-col gap-1 border-t border-borda-sutil py-2 first:border-t-0">
                  <span className="codigo text-tinta">{v.equipamento}</span>
                  <span className="txt-apoio text-tinta-secundaria">{`${quando(v.desde)} a ${v.ate ? quando(v.ate) : 'agora'}`}</span>
                  <span className="txt-apoio text-tinta-secundaria">
                    {conferenciaEscrita({ leituraDoPainel: v.leituraDoPainel, leituraDoDispositivo: v.leituraDoDispositivo, diferenca: v.diferenca, medidor: d.medidor })}
                  </span>
                </li>
              ))}
            </ul>
          ),
        },
      ]}
      acoes={
        podeConferir && d.vinculoId
          ? [
              <Botao key="conferir" nivel="primario" onClick={aoConferir}>
                <Check aria-hidden="true" className="size-[var(--icone-controle)]" />
                Conferir medidor
              </Botao>,
            ]
          : []
      }
      {...(podeConferir && d.vinculoId ? { notaDasAcoes: 'Só depois da conferência a última leitura de cada dia fechado vira leitura da máquina.' } : {})}
      aoFechar={aoFechar}
    />
  );
}

/* ================================================================ a fila */

const rotaDaFila = getRouteApi('/operacao/telemetria/fila');

const COLUNAS_DA_FILA: readonly Coluna<MensagemDaFila>[] = [
  {
    chave: 'recebida',
    rotulo: 'Recebida',
    celula: (m) => (
      <span className="flex flex-col">
        <span className="txt-corpo tabular-nums text-tinta">{quando(m.recebidaEm)}</span>
        <TextoDaCelula texto={`Lida ${quando(m.lidaEm, 'sem instante')}`} classe="txt-apoio text-tinta-secundaria" />
      </span>
    ),
  },
  {
    chave: 'dispositivo',
    rotulo: 'Dispositivo',
    celula: (m) => (
      <span className="flex flex-col">
        <span className="codigo text-tinta">{m.identificador}</span>
        <TextoDaCelula texto={m.equipamento ?? m.integracao} classe="txt-apoio text-tinta-secundaria" />
      </span>
    ),
  },
  {
    chave: 'campo',
    rotulo: 'Campo',
    celula: (m) => (
      <span className="flex flex-col">
        <span className="txt-corpo text-tinta">{rotuloDoTipo(m.tipo)}</span>
        <TextoDaCelula texto={valorEscrito(m.tipo, m)} classe="txt-apoio tabular-nums text-tinta-secundaria" />
      </span>
    ),
  },
  { chave: 'situacao', rotulo: 'Situação', alinhamento: 'status', celula: (m) => <SeloDaTelemetria {...situacaoNaFila(m)} /> },
  {
    chave: 'fazer',
    rotulo: 'O que fazer',
    quebra: 'duas-linhas',
    celula: (m) => <TextoDaCelula texto={oQueFazer(m)} classe="txt-apoio text-tinta" linhas={2} />,
  },
];

const COLUNAS_DOS_LOTES: readonly Coluna<LoteRecebido>[] = [
  {
    chave: 'recebido',
    rotulo: 'Recebido',
    celula: (l) => (
      <span className="flex flex-col">
        <span className="txt-corpo tabular-nums text-tinta">{quando(l.recebidoEm)}</span>
        <TextoDaCelula texto={`${l.integracao} · chave ${l.prefixo}`} classe="txt-apoio text-tinta-secundaria" />
      </span>
    ),
  },
  { chave: 'mensagens', rotulo: 'Mensagens', alinhamento: 'numero', celula: (l) => <span className="txt-corpo tabular-nums">{formatarInteiro(l.mensagens)}</span> },
  { chave: 'aceitas', rotulo: 'Aceitas', alinhamento: 'numero', celula: (l) => <span className="txt-corpo tabular-nums">{formatarInteiro(l.aceitas)}</span> },
  { chave: 'fila', rotulo: 'Para a fila', alinhamento: 'numero', celula: (l) => <span className="txt-corpo tabular-nums">{formatarInteiro(l.pendentes + l.recusadas)}</span> },
  { chave: 'repetidas', rotulo: 'Repetidas', alinhamento: 'numero', prioridade: 'secundaria', celula: (l) => <span className="txt-corpo tabular-nums">{formatarInteiro(l.duplicadas)}</span> },
  {
    chave: 'duracao',
    rotulo: 'Duração',
    alinhamento: 'numero',
    prioridade: 'secundaria',
    celula: (l) => (l.duracaoMs === null ? <span className="txt-apoio">—</span> : <Medida valor={formatarInteiro(l.duracaoMs)} unidade="ms" />),
  },
];

export function PaginaFilaDaTelemetria() {
  const busca = rotaDaFila.useSearch();
  const navegar = rotaDaFila.useNavigate();
  const situacao: FiltroDaFila = busca.situacao ?? 'todas';
  const pagina = busca.pagina ?? 1;
  const fila = useFila(situacao, pagina);
  const pode = usePermissoes();
  const reprocessar = useReprocessar();
  const [aberta, setAberta] = useState<string | null>(null);
  const [descartando, setDescartando] = useState<MensagemDaFila | null>(null);
  const linhas = fila.data?.mensagens ?? [];
  const selecionada = linhas.find((m) => m.id === aberta) ?? null;
  const total = fila.data?.total ?? 0;
  const porPagina = fila.data?.porPagina ?? 100;
  const ir = (s: FiltroDaFila, p: number) => {
    setAberta(null);
    void navegar({ search: { ...(s === 'todas' ? {} : { situacao: s }), ...(p > 1 ? { pagina: p } : {}) } });
  };
  return (
    <>
      <HubDaTelemetria>
        <div className="flex flex-wrap items-end gap-4">
          <div className="w-72 max-w-full">
            <CampoSelecao
              rotulo="Situação"
              valor={situacao}
              aplicado={situacao !== 'todas'}
              aoMudar={(v) => { ir(v as FiltroDaFila, 1); }}
              opcoes={FILTROS_DA_FILA.map((f) => ({
                valor: f,
                rotulo: fila.data ? `${ROTULO_DO_FILTRO[f]} (${formatarInteiro(fila.data.contagem[f])})` : ROTULO_DO_FILTRO[f],
              }))}
            />
          </div>
        </div>
        <Tabela
          rotulo="Fila da telemetria"
          colunas={COLUNAS_DA_FILA}
          linhas={linhas}
          chaveDaLinha={(m) => m.id}
          alturaDupla
          selecionada={aberta}
          aoSelecionar={(m) => { setAberta(m.id); }}
          carregando={fila.isPending}
          erro={fila.isError ? <ErroDaLista oQue="a fila da telemetria" erro={fila.error} aoTentar={() => { void fila.refetch(); }} /> : null}
          vazio={
            situacao === 'todas' ? (
              <EstadoVazio icone={Check} titulo="A fila está vazia" descricao="Tudo o que chegou entrou, ou nada chegou ainda. As mensagens que pedirem alguém aparecem aqui, com o que fazer." />
            ) : (
              <EstadoVazio
                icone={SearchX}
                titulo={`Nenhuma mensagem em ${ROTULO_DO_FILTRO[situacao].toLowerCase()}`}
                descricao="Limpe o filtro para ver a fila inteira."
                acao={
                  <Botao nivel="secundario" onClick={() => { ir('todas', 1); }}>
                    Limpar filtro
                  </Botao>
                }
              />
            )
          }
          rodape={
            <RodapeTabela
              primeiro={total === 0 ? 0 : (pagina - 1) * porPagina + 1}
              ultimo={Math.min(pagina * porPagina, total)}
              total={total}
              pagina={pagina}
              paginas={Math.max(1, Math.ceil(total / porPagina))}
              porPagina={porPagina}
              opcoesPorPagina={[porPagina]}
              aoMudarPagina={(p) => { ir(situacao, p); }}
              aoMudarPorPagina={() => undefined}
            />
          }
        />
        <section className="flex flex-col gap-3">
          <h2 className="txt-secao text-tinta">Os últimos lotes recebidos</h2>
          <Tabela
            rotulo="Lotes recebidos"
            colunas={COLUNAS_DOS_LOTES}
            linhas={fila.data?.lotes ?? []}
            chaveDaLinha={(l) => l.id}
            alturaDupla
            carregando={fila.isPending}
            erro={fila.isError ? <ErroDaLista oQue="os lotes recebidos" erro={fila.error} aoTentar={() => { void fila.refetch(); }} /> : null}
            vazio={<EstadoVazio icone={Hourglass} titulo="Nenhum lote recebido" descricao="Quando o provedor mandar a primeira chamada com a chave, o lote aparece aqui com o que entrou e o que foi para a fila." />}
          />
        </section>
      </HubDaTelemetria>
      {selecionada ? (
        <PainelDetalhe
          key={selecionada.id}
          codigo={selecionada.idExterno}
          titulo={`${rotuloDoTipo(selecionada.tipo)} · ${selecionada.identificador}`}
          subtitulo={selecionada.integracao}
          marcadores={<SeloDaTelemetria {...situacaoNaFila(selecionada)} />}
          dadosChave={
            <>
              <DadoChave rotulo="Valor">{valorEscrito(selecionada.tipo, selecionada)}</DadoChave>
              <DadoChave rotulo="Unidade recebida">{selecionada.unidadeRecebida ?? '—'}</DadoChave>
              <DadoChave rotulo="Lida em">{quando(selecionada.lidaEm, 'Sem instante válido')}</DadoChave>
              <DadoChave rotulo="Máquina">{selecionada.equipamento ?? 'Nenhuma'}</DadoChave>
            </>
          }
          abas={[
            { id: 'fazer', rotulo: 'O que fazer', conteudo: <p className="txt-corpo text-pretty text-tinta">{oQueFazer(selecionada)}</p> },
            {
              id: 'bruto',
              rotulo: 'Como veio',
              conteudo: <pre className="mono txt-apoio overflow-auto rounded-controle bg-rebaixada p-3 text-tinta">{JSON.stringify(selecionada.bruto, null, 2)}</pre>,
            },
          ]}
          acoes={
            pode.conferir
              ? [
                  ...(podeReprocessar(selecionada)
                    ? [
                        <Botao
                          key="reprocessar"
                          nivel="primario"
                          disabled={reprocessar.isPending}
                          onClick={() => {
                            reprocessar.mutate([selecionada.id], { onSuccess: () => { setAberta(null); } });
                          }}
                        >
                          <RefreshCw aria-hidden="true" className="size-[var(--icone-controle)]" />
                          {reprocessar.isPending ? 'Reprocessando…' : 'Reprocessar'}
                        </Botao>,
                      ]
                    : []),
                  <Botao key="descartar" nivel="destrutivo" onClick={() => { setDescartando(selecionada); }}>
                    Descartar mensagem
                  </Botao>,
                ]
              : []
          }
          {...(pode.conferir
            ? { notaDasAcoes: podeReprocessar(selecionada) ? 'Reprocesse depois de resolver o que a frase pede; descarte o que não serve.' : 'A recusada volta corrigida pelo provedor; descarte esta.' }
            : {})}
          aoFechar={() => { setAberta(null); }}
        />
      ) : null}
      {pode.conferir ? (
        <JanelaDeDescarte
          alvo={descartando}
          aoFechar={() => {
            setDescartando(null);
            setAberta(null);
          }}
        />
      ) : null}
    </>
  );
}

/* ================================================================ a conciliação */

const rotaDaConciliacao = getRouteApi('/operacao/telemetria/conciliacao');

/** A diferença, com sinal; em vermelho só quando o próprio valor é o alerta (passou da tolerância). */
function Diferenca({ valor, unidade, alerta }: { readonly valor: number; readonly unidade: string; readonly alerta: boolean }) {
  return (
    <span className={`numero ${alerta ? 'text-erro-texto' : 'text-tinta'}`}>
      {`${valor > 0 ? '+' : ''}${formatarDecimal(valor, 1)}`}
      <span className="txt-unidade">{` ${unidade}`}</span>
    </span>
  );
}

function horas(v: number | null): ReactNode {
  return v === null ? <span className="txt-apoio text-tinta-secundaria">—</span> : <Medida valor={formatarDecimal(v, 1)} unidade="h" />;
}

function colunasDosDias(leOperacao: boolean): readonly Coluna<DiaConciliado>[] {
  return [
    {
      chave: 'maquina',
      rotulo: 'Máquina e dia',
      celula: (d) => (
        <span className="flex flex-col">
          <span className="codigo text-tinta">{d.equipamento}</span>
          <TextoDaCelula texto={formatarDia(d.dia)} classe="txt-apoio text-tinta-secundaria" />
        </span>
      ),
    },
    { chave: 'tel', rotulo: 'Motor (telemetria)', alinhamento: 'numero', celula: (d) => horas(d.horasMotorTelemetria) },
    ...(leOperacao
      ? [
          { chave: 'ap', rotulo: 'Motor (apontado)', alinhamento: 'numero' as const, celula: (d: DiaConciliado) => horas(d.horasMotorApontadas) },
          { chave: 'turno', rotulo: 'Turno', alinhamento: 'numero' as const, prioridade: 'secundaria' as const, celula: (d: DiaConciliado) => horas(d.horasTurno) },
          {
            chave: 'dif',
            rotulo: 'Diferença',
            alinhamento: 'numero' as const,
            celula: (d: DiaConciliado) =>
              d.diferenca === null ? (
                <span className="txt-apoio text-tinta-secundaria">{d.apontamentos === 0 ? 'Sem apontamento' : '—'}</span>
              ) : (
                <Diferenca valor={d.diferenca} unidade="h" alerta={d.acimaDaTolerancia === true} />
              ),
          },
        ]
      : []),
    { chave: 'ociosas', rotulo: 'Ociosas', alinhamento: 'numero', prioridade: 'secundaria', celula: (d) => horas(d.horasOciosas) },
    ...(leOperacao ? [{ chave: 'fila', rotulo: 'Na conciliação de horas', celula: (d: DiaConciliado) => <span className="txt-apoio text-tinta">{divergenciaEscrita(d)}</span> }] : []),
  ];
}

const COLUNAS_DO_CONSUMO: readonly Coluna<ConsumoConciliado>[] = [
  {
    chave: 'maquina',
    rotulo: 'Máquina',
    celula: (c) => (
      <span className="flex flex-col">
        <span className="codigo text-tinta">{c.equipamento}</span>
        <TextoDaCelula texto={`${formatarInteiro(c.diasComDado)} ${c.diasComDado === 1 ? 'dia com dado' : 'dias com dado'}`} classe="txt-apoio text-tinta-secundaria" />
      </span>
    ),
  },
  { chave: 'tel', rotulo: 'Consumido (telemetria)', alinhamento: 'numero', celula: (c) => <Medida valor={formatarDecimal(c.litrosTelemetria, 1)} unidade="L" /> },
  {
    chave: 'ab',
    rotulo: 'Abastecido',
    alinhamento: 'numero',
    celula: (c) => (c.litrosAbastecidos === null ? <span className="txt-apoio text-tinta-secundaria">Sem acesso</span> : <Medida valor={formatarDecimal(c.litrosAbastecidos, 1)} unidade="L" />),
  },
  {
    chave: 'dif',
    rotulo: 'Diferença',
    alinhamento: 'numero',
    celula: (c) =>
      c.diferenca === null ? (
        <span className="txt-apoio text-tinta-secundaria">—</span>
      ) : (
        <Diferenca valor={c.diferenca} unidade="L" alerta={c.acimaDaTolerancia === true} />
      ),
  },
  {
    chave: 'tanque',
    rotulo: 'Tanque',
    alinhamento: 'numero',
    prioridade: 'secundaria',
    celula: (c) => (c.capacidadeL === null ? <span className="txt-apoio text-tinta-secundaria">Sem capacidade</span> : <Medida valor={formatarDecimal(c.capacidadeL, 1)} unidade="L" />),
  },
];

export function PaginaConciliacaoDaTelemetria() {
  const base = useId();
  const busca = rotaDaConciliacao.useSearch();
  const navegar = rotaDaConciliacao.useNavigate();
  const pagina = busca.pagina ?? 1;
  const c = useConciliacaoDaTelemetria(busca.de, busca.ate, pagina);
  const dados = c.data;
  const ir = (de: string | undefined, ate: string | undefined, p: number) => {
    void navegar({ search: { ...(de ? { de } : {}), ...(ate ? { ate } : {}), ...(p > 1 ? { pagina: p } : {}) } });
  };
  const de = busca.de ?? dados?.periodo.de ?? '';
  const ate = busca.ate ?? dados?.periodo.ate ?? '';
  return (
    <HubDaTelemetria>
      <div className="flex flex-wrap items-end gap-4">
        <Campo id={`${base}-de`} rotulo="De">
          <input id={`${base}-de`} type="date" className="controle" value={de} onChange={(e) => { ir(e.target.value || undefined, busca.ate, 1); }} />
        </Campo>
        <Campo id={`${base}-ate`} rotulo="Até">
          <input id={`${base}-ate`} type="date" className="controle" value={ate} onChange={(e) => { ir(busca.de, e.target.value || undefined, 1); }} />
        </Campo>
        {dados?.leOperacao ? (
          <Link to="/operacao/conciliacao" search={{ de, ate, situacao: 'aberta' }} className="botao botao-secundario">
            Abrir a conciliação de horas
          </Link>
        ) : null}
      </div>
      <section className="flex flex-col gap-3">
        <h2 className="txt-secao text-tinta">Quantas horas de motor a telemetria contou, e quantas foram apontadas?</h2>
        {dados ? (
          <p className="txt-apoio max-w-[80ch] text-pretty text-tinta-secundaria">
            {`${periodoEscrito(dados.periodo.de, dados.periodo.ate)} · tolerância de ${formatarDecimal(dados.premissas.toleranciaH, 1)} h, a da conciliação de horas. ${dados.premissas.horas}`}
          </p>
        ) : (
          <span className="esqueleto block h-4 w-2/3" aria-hidden="true" />
        )}
        {dados && !dados.leOperacao ? (
          <p className="txt-apoio text-tinta-secundaria">Seu perfil não vê a operação: as horas apontadas ficam de fora. Peça ao administrador o perfil Gestor operacional.</p>
        ) : null}
        <Tabela
          rotulo="Horas de motor por máquina e dia"
          colunas={colunasDosDias(dados?.leOperacao ?? true)}
          linhas={dados?.dias ?? []}
          chaveDaLinha={(d) => `${d.equipamentoId}-${d.dia}`}
          alturaDupla
          carregando={c.isPending}
          erro={c.isError ? <ErroDaLista oQue="a conciliação da telemetria" erro={c.error} aoTentar={() => { void c.refetch(); }} /> : null}
          vazio={
            <EstadoVazio
              icone={Gauge}
              titulo="Nenhuma hora de motor da telemetria no período"
              descricao="A conta precisa da última leitura do horímetro do dia e da do dia anterior. Escolha outro período, ou confira em Sinal se os dispositivos estão mandando."
            />
          }
          rodape={
            dados ? (
              <RodapeTabela
                primeiro={dados.total === 0 ? 0 : (pagina - 1) * dados.porPagina + 1}
                ultimo={Math.min(pagina * dados.porPagina, dados.total)}
                total={dados.total}
                pagina={pagina}
                paginas={Math.max(1, Math.ceil(dados.total / dados.porPagina))}
                porPagina={dados.porPagina}
                opcoesPorPagina={[dados.porPagina]}
                aoMudarPagina={(p) => { ir(busca.de, busca.ate, p); }}
                aoMudarPorPagina={() => undefined}
              />
            ) : null
          }
        />
      </section>
      <section className="flex flex-col gap-3">
        <h2 className="txt-secao text-tinta">O combustível que a telemetria contou bate com o abastecido?</h2>
        <p className="txt-apoio max-w-[80ch] text-pretty text-tinta-secundaria">
          {dados ? `Tolerância de ${formatarDecimal(dados.premissas.toleranciaConsumoPct, 1)}%. ${dados.premissas.consumo}` : ''}
        </p>
        <Tabela
          rotulo="Consumo telemetrado e abastecido"
          colunas={COLUNAS_DO_CONSUMO}
          linhas={dados?.consumo ?? []}
          chaveDaLinha={(x) => x.equipamentoId}
          alturaDupla
          carregando={c.isPending}
          erro={c.isError ? <ErroDaLista oQue="o consumo da telemetria" erro={c.error} aoTentar={() => { void c.refetch(); }} /> : null}
          vazio={
            <EstadoVazio
              icone={Fuel}
              titulo="Nenhum consumo da telemetria no período"
              descricao="Só as integrações que mandam o combustível consumido entram aqui. Declare o campo na integração, se o dispositivo manda."
            />
          }
        />
      </section>
    </HubDaTelemetria>
  );
}

/* ================================================================ as integrações */

export function PaginaIntegracoesDaTelemetria() {
  const integracoes = useIntegracoes();
  const pode = usePermissoes();
  const [nova, setNova] = useState(false);
  const [editando, setEditando] = useState<Integracao | null>(null);
  const [emitindo, setEmitindo] = useState<Integracao | null>(null);
  const [revogando, setRevogando] = useState<ChaveDaIntegracao | null>(null);
  const [novoDispositivo, setNovoDispositivo] = useState(false);
  const [ligando, setLigando] = useState<DispositivoCadastrado | null>(null);
  const [encerrando, setEncerrando] = useState<DispositivoCadastrado | null>(null);
  const [conferindo, setConferindo] = useState<DispositivoCadastrado | null>(null);
  const [regras, setRegras] = useState(false);
  const d = integracoes.data;
  const lista = d?.integracoes ?? [];

  const colunas: readonly Coluna<DispositivoCadastrado>[] = [
    {
      chave: 'dispositivo',
      rotulo: 'Dispositivo',
      celula: (x) => (
        <span className="flex flex-col">
          <span className="codigo text-tinta">{x.identificador}</span>
          <TextoDaCelula texto={`${x.integracao}${x.ativo ? '' : ' · desativado'}`} classe="txt-apoio text-tinta-secundaria" />
        </span>
      ),
    },
    {
      chave: 'maquina',
      rotulo: 'Máquina',
      celula: (x) => (
        <span className="flex flex-col">
          <span className="codigo text-tinta">{x.equipamento ?? 'Sem máquina'}</span>
          <TextoDaCelula texto={x.desde ? `desde ${quando(x.desde)}` : 'Ligue a uma máquina'} classe="txt-apoio text-tinta-secundaria" />
        </span>
      ),
    },
    {
      chave: 'medidor',
      rotulo: 'Medidor',
      quebra: 'duas-linhas',
      celula: (x) => <TextoDaCelula texto={x.equipamento ? conferenciaEscrita(x) : '—'} classe="txt-apoio text-tinta" linhas={2} />,
    },
    {
      chave: 'contador',
      rotulo: 'Último contador',
      alinhamento: 'numero',
      celula: (x) => (x.ultimoContador === null ? <span className="txt-apoio text-tinta-secundaria">—</span> : <Medida valor={formatarDecimal(x.ultimoContador, 1)} unidade={x.medidor === 'odometro' ? 'km' : 'h'} />),
    },
  ];

  return (
    <>
      <HubDaTelemetria
        acoes={
          pode.gerir ? (
            <>
              <Botao nivel="secundario" disabled={lista.length === 0} onClick={() => { setNovoDispositivo(true); }}>
                Cadastrar dispositivo
              </Botao>
              <Botao nivel="primario" onClick={() => { setNova(true); }}>
                <Plus aria-hidden="true" className="size-[var(--icone-controle)]" />
                Cadastrar integração
              </Botao>
            </>
          ) : undefined
        }
      >
        <section className="cartao flex flex-col gap-2 p-6">
          <h2 className="txt-secao text-tinta">Como o provedor manda</h2>
          <p className="txt-apoio max-w-[80ch] text-pretty text-tinta-secundaria">
            {`POST no endereço da API em ${ROTA_DA_ENTRADA.padrao} (formato padrão) ou ${ROTA_DA_ENTRADA.aemp} (AEMP 2.0, ISO 15143-3), com a chave no cabeçalho Authorization: Bearer. Até 10.000 mensagens por chamada; o instante com o fuso e a unidade escrita. A mesma mensagem mandada de novo não entra duas vezes.`}
          </p>
          <pre className="mono txt-apoio overflow-auto rounded-controle bg-rebaixada p-4 text-tinta">{EXEMPLO_DO_FORMATO}</pre>
        </section>

        {integracoes.isPending ? (
          <div className="cartao flex flex-col gap-3 p-6" aria-hidden="true">
            <span className="esqueleto block h-5 w-1/3" />
            <span className="esqueleto block h-4 w-2/3" />
          </div>
        ) : integracoes.isError ? (
          <ErroDaLista oQue="as integrações" erro={integracoes.error} aoTentar={() => { void integracoes.refetch(); }} />
        ) : lista.length === 0 ? (
          <EstadoVazio
            icone={Gauge}
            titulo="Nenhuma integração cadastrada"
            descricao={
              pode.gerir
                ? 'Não há telemetria real ainda. Cadastre o provedor (ou o rastreador), emita a chave e entregue a ele com o formato acima.'
                : 'Não há telemetria real ainda. Quem cadastra o provedor é o perfil Administrador.'
            }
            acao={pode.gerir ? <Botao nivel="secundario" onClick={() => { setNova(true); }}>Cadastrar integração</Botao> : undefined}
          />
        ) : (
          <div className="grid gap-4 min-[1280px]:grid-cols-2">
            {lista.map((i) => {
              const chaves = (d?.chaves ?? []).filter((k) => k.integracaoId === i.id);
              return (
                <section key={i.id} className="cartao flex flex-col gap-3 p-6">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex min-w-0 flex-col">
                      <h3 className="txt-secao text-tinta">{i.nome}</h3>
                      <span className="txt-apoio text-tinta-secundaria">{`${FORMATOS[i.formato]} · a cada ${formatarInteiro(i.frequenciaMin)} min · ${formatarInteiro(i.dispositivos)} ${i.dispositivos === 1 ? 'dispositivo' : 'dispositivos'}`}</span>
                    </div>
                    <SeloDaTelemetria rotulo={i.ativa ? 'Ativa' : 'Desativada'} tom={i.ativa ? 'sucesso' : 'neutro'} />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {i.campos.map((c) => (
                      <span key={c} className="txt-legenda rounded-etiqueta bg-rebaixada px-2 py-1 text-tinta">
                        {ROTULO_DO_TIPO[c]}
                      </span>
                    ))}
                  </div>
                  <p className="txt-apoio text-tinta-secundaria">
                    {`Último lote: ${quando(i.ultimoLoteEm, 'nenhum ainda')} · licença ${i.licencaAte ? `até ${formatarDia(i.licencaAte)}` : 'sem data informada'}`}
                  </p>
                  <ul className="flex flex-col" aria-label={`Chaves de ${i.nome}`}>
                    {chaves.length === 0 ? <li className="txt-apoio text-tinta-secundaria">Nenhuma chave emitida: o provedor ainda não tem como mandar.</li> : null}
                    {chaves.map((k) => (
                      <li key={k.id} className="flex flex-wrap items-center justify-between gap-2 border-t border-borda-sutil py-2 first:border-t-0">
                        <span className="flex min-w-0 flex-col">
                          <span className="codigo text-tinta">{`ctt_${k.prefixo}_…`}</span>
                          <span className="txt-apoio text-tinta-secundaria">
                            {k.revogadaEm
                              ? `Revogada em ${quando(k.revogadaEm)} · ${k.motivoRevogacao ?? ''}`
                              : `Emitida em ${quando(k.criadaEm)} por ${k.criadaPor ?? '—'} · último uso ${quando(k.ultimoUsoEm, 'nunca')}`}
                          </span>
                        </span>
                        {pode.gerir && !k.revogadaEm ? (
                          <Botao nivel="fantasma" tamanho="p" onClick={() => { setRevogando(k); }}>
                            Revogar chave
                          </Botao>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                  {pode.gerir ? (
                    <div className="flex flex-wrap gap-2">
                      <Botao nivel="secundario" tamanho="p" onClick={() => { setEmitindo(i); }}>
                        Emitir chave
                      </Botao>
                      <Botao nivel="fantasma" tamanho="p" onClick={() => { setEditando(i); }}>
                        Alterar integração
                      </Botao>
                    </div>
                  ) : null}
                </section>
              );
            })}
          </div>
        )}

        <section className="flex flex-col gap-3">
          <h2 className="txt-secao text-tinta">Em que máquina está cada dispositivo?</h2>
          <Tabela
            rotulo="Dispositivos cadastrados"
            colunas={colunas}
            linhas={d?.dispositivos ?? []}
            chaveDaLinha={(x) => x.id}
            alturaDupla
            carregando={integracoes.isPending}
            erro={integracoes.isError ? <ErroDaLista oQue="os dispositivos" erro={integracoes.error} aoTentar={() => { void integracoes.refetch(); }} /> : null}
            vazio={
              <EstadoVazio
                icone={Gauge}
                titulo="Nenhum dispositivo cadastrado"
                descricao={pode.gerir ? 'Cadastre o dispositivo pelo identificador que o provedor manda, e ligue-o à máquina.' : 'Quem cadastra os dispositivos é o perfil Administrador.'}
              />
            }
            {...(pode.gerir || pode.conferir
              ? {
                  acoes: (x: DispositivoCadastrado) => (
                    <span className="flex gap-1">
                      {pode.gerir && !x.vinculoId ? (
                        <Botao nivel="fantasma" tamanho="p" onClick={() => { setLigando(x); }}>
                          <ArrowLeftRight aria-hidden="true" className="size-[var(--icone-controle)]" />
                          Ligar à máquina
                        </Botao>
                      ) : null}
                      {pode.conferir && x.vinculoId ? (
                        <Botao nivel="fantasma" tamanho="p" onClick={() => { setConferindo(x); }}>
                          Conferir medidor
                        </Botao>
                      ) : null}
                      {pode.gerir && x.vinculoId ? (
                        <Botao nivel="fantasma" tamanho="p" onClick={() => { setEncerrando(x); }}>
                          Encerrar vínculo
                        </Botao>
                      ) : null}
                    </span>
                  ),
                }
              : {})}
            rodape={
              <p className="txt-apoio border-t border-borda-sutil px-4 py-2 text-tinta-secundaria">
                {d && d.totalDeDispositivos > (d.dispositivos.length || 0)
                  ? `São ${formatarInteiro(d.totalDeDispositivos)} dispositivos; a lista traz os ${formatarInteiro(d.dispositivos.length)} primeiros.`
                  : `${formatarInteiro(d?.dispositivos.length ?? 0)} ${(d?.dispositivos.length ?? 0) === 1 ? 'dispositivo' : 'dispositivos'}`}
              </p>
            }
          />
        </section>

        {d ? (
          <section className="cartao flex flex-col gap-2 p-6">
            <h2 className="txt-secao text-tinta">As regras da telemetria</h2>
            <p className="txt-apoio max-w-[80ch] text-pretty text-tinta-secundaria">
              {`Sem sinal depois de ${formatarDecimal(d.regras.semSinalVezes, 1)} vezes a frequência da integração (no mínimo 30 minutos). Ociosidade avisa a partir de ${formatarDecimal(d.regras.ociosidadePct, 1)}% das horas de motor do dia. O consumo chama atenção acima de ${formatarDecimal(d.regras.toleranciaConsumoPct, 1)}% de diferença.${d.regras.padrao ? ' São as regras recomendadas: ninguém salvou outras.' : ''}`}
            </p>
            {pode.gerir ? (
              <div>
                <Botao nivel="secundario" tamanho="p" onClick={() => { setRegras(true); }}>
                  Alterar regras
                </Botao>
              </div>
            ) : null}
          </section>
        ) : null}
      </HubDaTelemetria>

      {pode.gerir ? (
        <>
          <JanelaDeIntegracao key={nova ? 'nova' : 'fechada'} aberta={nova} editando={null} aoFechar={() => { setNova(false); }} />
          <JanelaDeIntegracao key={editando?.id ?? 'sem'} aberta={editando !== null} editando={editando} aoFechar={() => { setEditando(null); }} />
          <JanelaDaChaveNova integracao={emitindo} aoFechar={() => { setEmitindo(null); }} />
          <JanelaDeRevogacao chave={revogando} aoFechar={() => { setRevogando(null); }} />
          <JanelaDeDispositivo aberta={novoDispositivo} integracoes={lista} aoFechar={() => { setNovoDispositivo(false); }} />
          <JanelaDeLigar dispositivo={ligando} aoFechar={() => { setLigando(null); }} />
          <JanelaDeEncerrar dispositivo={encerrando} aoFechar={() => { setEncerrando(null); }} />
          {d ? <JanelaDasRegras key={regras ? 'aberta' : 'fechada'} aberta={regras} regras={d.regras} aoFechar={() => { setRegras(false); }} /> : null}
        </>
      ) : null}
      {pode.conferir ? (
        <JanelaDeConferencia
          alvo={
            conferindo?.vinculoId && conferindo.equipamento
              ? { vinculoId: conferindo.vinculoId, equipamento: conferindo.equipamento, medidor: conferindo.medidor, ultimoContador: conferindo.ultimoContador }
              : null
          }
          aoFechar={() => { setConferindo(null); }}
        />
      ) : null}
    </>
  );
}
